import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { NavigationService } from "abreact/incubator/ui/app/NavigationService";
import { AbSDKRouteHandler } from "abreact/incubator/ui/app/router/simple/AbSDKRouteHandler";
import { RouteSpecHelper } from "abreact/incubator/ui/app/router/simple/RouteSpecHelper";
import { calcParamNames } from "abweb/service/router/RouteParser";
import { Assert } from "abweb/utils/Assert";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
const LOG = LogUtils.getLogger(__filename);
export function isAbSDKRegisteredRoute(route) {
  // runtime 'instanceof' of interfaces (e.g., RegisteredRoute) is not supported by typescript, but for now,
  // only AbSDKRoute implements RegisteredRoute, so just check for AbSDKRoute
  return ClassUtils.isInstanceOf(route, AbSDKRegisteredRoute);
}

/**
 * This is a class to handle routes where there is a bidirectional mapping between route parameters and view properties.
 */
export class AbSDKRegisteredRoute {
  constructor(routeSpec) {
    _defineProperty(this, "routeSpec", void 0);
    _defineProperty(this, "routeSpecHelper", void 0);
    _defineProperty(this, "supportedRoutes", void 0);
    _defineProperty(this, "routeInfo", void 0);
    this.routeSpec = routeSpec;
    this.routeSpecHelper = new RouteSpecHelper(routeSpec);
    this.supportedRoutes = this.buildRoutes();
    this.routeInfo = this.supportedRoutes[0];
  }

  /**
   * @inheritDoc
   */
  goto(props, routeGotoOptions) {
    let {
      routeRenderContext,
      addCurrentRouteToBrowserHistory
    } = routeGotoOptions;
    addCurrentRouteToBrowserHistory = addCurrentRouteToBrowserHistory ?? true;
    const rrc = routeRenderContext ?? NavigationService.getSingleton().mainAreaRouteRenderContext;
    const newRoute = this.buildRouteFromProps(props);
    return NavigationService.getSingleton().changeView({
      newRoute,
      routeRenderContext: rrc,
      addCurrentRouteToBrowserHistory
    });
  }

  /**
   * @inheritDoc
   */
  href(props) {
    const browserRoute = this.routeSpecHelper.buildRouteFromProps(props);
    return "#" + browserRoute.route;
  }

  /**
   * @inheritDoc
   */
  buildRouteFromProps(props) {
    return this.routeSpecHelper.buildRouteFromProps(props);
  }
  buildRouteFromParams(routeParams) {
    return this.routeSpecHelper.buildRouteFromParams(routeParams);
  }

  /**
   * @inheritDoc
   */
  getRouteInfo() {
    return this.routeInfo;
  }
  buildRoutes() {
    const routeInfos = [];
    this.routeSpecHelper.registeredRoutes.forEach(routeTemplate => {
      const routeInfo = this.createRouteInfo(routeTemplate);
      routeInfos.push(routeInfo);
    });
    return routeInfos;
  }
  createRouteInfo(routeTemplate) {
    // Use new RouteHandler instance on every major route to prevent mem leaks
    const routeHandlerFactory = () => Promise.resolve(new AbSDKRouteHandler(this.routeSpecHelper));
    const newRouteFn = this.buildReactRouteFromParamsFn();
    const routeCallback = (...args) => {
      const navService = NavigationService.getSingleton();
      return navService.changeView({
        newRoute: newRouteFn(...args),
        // Always use the 'mainArea' routeRenderContext that is builtin to the navService for browser routes
        routeRenderContext: navService.mainAreaRouteRenderContext,
        // ABSDK-2874: never add to browserHistory if it is browserInitiated, as browser url has already transitioned
        // to new route, and added prev url to appropriate browser back/forward history stacks.
        addCurrentRouteToBrowserHistory: false,
        browserInitiated: true
      });
    };
    return {
      routeTemplate,
      routeHandlerFactory,
      routeCallback
    };
  }
  buildReactRouteFromParamsFn() {
    const routeTemplate = this.routeSpec.routeTemplate;
    const paramNames = calcParamNames(routeTemplate);
    const totalNumArgs = paramNames.length + 1; // routeExtraParams is always last (even if there aren't any)

    return (...routeArgs) => {
      Assert.assert(routeArgs?.length === totalNumArgs, `Expected ${totalNumArgs} but received ${routeArgs?.length} for route: ${routeTemplate}`);
      const routeParams = {};
      paramNames.forEach((paramName, i) => routeParams[paramName] = routeArgs[i]);
      const routeExtraParams = routeArgs[routeArgs.length - 1]; // last arg is always extra params
      return this.routeSpecHelper.buildRouteFromParams({
        routeParams,
        routeExtraParams
      });
    };
  }
}
//# sourceMappingURL=AbSDKRegisteredRoute.js.map
